<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromEditing" />
    <div v-else>
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <div class="row">
        <div class="col-12">
          <validation-observer ref="validationObserver">
            <div class="col-12 bg-white rounded shadow rounded p-4">
              <div class="row">
                <div class="col-12 col-md-6 col-xl-4 pt-md-3 ml-xl-4">
                  <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="is-mobile">
                    <b-form-group :label="$t('Mobile Number for SMS Alerts')">
                      <b-form-input
                        v-model="leadAlertSettings.mobileSms"
                        type="tel"
                        :state="(dirty || validated) ? (valid ? null : false) : null"
                      />
                      <b-form-invalid-feedback :state="invalid">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                  <b-form-group>
                    <template slot="label">
                      <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0">
                        {{ $t('Account Email') }}
                        <router-link class="label-link" :to="{name: 'user-details'}">
                          {{ $t('Edit in User Details') }}
                        </router-link>
                      </legend>
                    </template>
                    <b-form-input v-model="accountEmail" readonly />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-6 col-xl-4 pt-md-3 ml-xl-4">
                  <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="email">
                    <b-form-group :label="$t('Email Leads to')">
                      <b-form-input
                        v-model="leadAlertSettings.leadEmails[0]"
                        :state="(dirty || validated) ? (valid ? null : false) : null"
                        :placeholder="$t('Account Email')"
                      />
                      <b-form-invalid-feedback :state="invalid">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                  <b-form-group v-for="(email, index) in additionalEmails" :key="index">
                    <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="email">
                      <label class="d-flex justify-content-between">
                        <span>{{ $t('Additional Email') }}</span>
                        <b-button class="p-0 font-weight-normal" variant="link" size="sm" @click="onRemoveAdditionalEmailClick(index + 1)">
                          <font-awesome-icon class="text-remove mr-1" :icon="'minus-circle'" />{{ $t('Remove') }}
                        </b-button>
                      </label>
                      <b-form-input v-model="leadAlertSettings.leadEmails[index + 1]" :state="(dirty || validated) ? (!!valid) : null" />
                      <b-form-invalid-feedback :state="invalid">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                  <b-button v-if="!reachedAdditionalEmailsCap" class="px-0 pt-0 pb-3" size="sm" variant="link" @click="onAdditionalEmailClick">
                    <font-awesome-icon class="text-success mr-1" :icon="'plus-circle'" />{{ $t('Add Additional Email') }}
                  </b-button>
                  <div v-else class="invalid-feedback d-block">
                    {{ $t('(You’ve reached the maximum number of additional emails.)') }}'
                  </div>
                </div>
              </div>
              <div class="row">
                <b-card class="col-12 col-md-6 col-xl-4 pt-md-3 ml-xl-4 mt-3 mt-md-0" :title="$t('Marketplace Lead Alerts')">
                  <p class="h5 font-weight-normal lead-alert-copy">
                    {{ $t('Receive marketplace alerts for all leads in your area') }}
                  </p>
                  <div class="row mb-3">
                    <div class="col-6">
                      <label class="ml-auto mb-0">{{ $t('Email') }}</label>
                    </div>
                    <div class="col-6">
                      <b-checkbox id="marketplace-email-enable" v-model="leadAlertSettings.marketplaceEmailEnabled" switch />
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-6">
                      <label class="ml-auto mb-0">{{ $t('SMS') }}</label>
                    </div>
                    <div class="col-6">
                      <b-checkbox id="marketplace-sms-enable" v-model="leadAlertSettings.marketplaceSmsEnabled" switch />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <label class="ml-auto mb-0">{{ $t('Alert Frequency') }}</label>
                    </div>
                    <div class="col-6">
                      <b-form-select
                        v-model="leadAlertSettings.marketplaceNotify"
                        size="sm"
                        class="mb-3"
                        :options="alertFrequencies"
                      />
                    </div>
                  </div>
                </b-card>
                <b-card class="col-12 col-md-6 col-xl-4 pt-md-3 ml-xl-4 mt-3 mt-md-0" :title="$t('New Lead Alerts')">
                  <p class="h5 font-weight-normal lead-alert-copy">
                    {{ $t('Receive live lead alerts as soon as they are qualified, be quick as opportunity to buy is limited') }}
                  </p>
                  <div class="row mb-3">
                    <div class="col-6">
                      <label class="ml-auto mb-0">{{ $t('Email') }}</label>
                    </div>
                    <div class="col-6">
                      <b-checkbox id="new-lead-email-enable" v-model="leadAlertSettings.leadsByEmail" switch />
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-6">
                      <label class="ml-auto mb-0">{{ $t('SMS') }}</label>
                    </div>
                    <div class="col-6">
                      <b-checkbox id="new-lead-sms-enable" v-model="leadAlertSettings.leadsBySms" switch />
                    </div>
                  </div>
                </b-card>
              </div>
            </div>
          </validation-observer>
        </div>
      </div>
      <div class="col-12 pl-0 mt-4 nav__sticky-footer">
        <div class="d-inline-block text-center submit-button">
          <b-button
            variant="primary"
            size="sm"
            :disabled="busy"
            :class="['animated-submit', submitClassObject, {'disabled': busy, 'animate': animateSubmitButton}]"
            type="submit"
            @click="onSave"
          >
            {{ $t('Save') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import notification from "@/imports/mixins/notification";
import BanEditProfile from "@/components/control/BanEditProfile";
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import submitAnimationHandler from "@/imports/mixins/submitAnimationHandler";

const ADDITIONAL_EMAIL_CAP = 5;

export default {
  name: "LeadAlerts",
  components: {PageHeading, ValidationObserver, ValidationProvider, BanEditProfile},
  mixins: [notification, isBannedFromEditing, submitAnimationHandler],
  data() {
    return {
      busy: false,
      savedSuccessfully: null,
    }
  },
  computed: {
    accountEmail: function() {
      return this.$store.state.company.COMPANY ? this.$store.state.company.COMPANY.company_user.email : '';
    },
    leadAlertSettings: function() {
      return this.$store.state.company.LEAD_ALERT_SETTINGS;
    },
    additionalEmails() {
      return this.leadAlertSettings.leadEmails.filter((val, index) => index > 0);
    },
    reachedAdditionalEmailsCap: function() {
      return this.leadAlertSettings.leadEmails.length === ADDITIONAL_EMAIL_CAP;
    },
    pageTitle() {
      return this.$t("Lead Alerts");
    },
    alertFrequencies() {
      return [
        { value: '', text: this.$t('Please choose'), disabled: true },
        { value: 1, text: this.$t('Hourly') },
        { value: 2, text: this.$t('7am & 5pm') },
        { value: 3, text: this.$t('7am') },
        { value: 4, text: this.$t('5pm') },
      ];
    },
  },
  methods: {
    onAdditionalEmailClick() {
      if (this.leadAlertSettings.leadEmails.length < ADDITIONAL_EMAIL_CAP) {
        this.leadAlertSettings.leadEmails.push('');
      }
    },
    onSave() {
      this.busy = true;
      this.$refs.validationObserver.validate().then(success => {
        if (!success) {
          this.busy = false;
          return;
        }

        this.leadAlertSettings.leadEmails = this.leadAlertSettings.leadEmails.filter((email) => email.trim() !== '');
        this.$store.dispatch('updateLeadAlertSettings', this.leadAlertSettings)
          .then((success) => {
            this.animateSubmitButton = true;
            this.notify(
              this.$bvToast,
              success ? this.$t('Your settings have been saved successfully') : this.$t('There was an error saving your settings'),
              null,
              success ? 'success' : 'danger'
            );
            this.enableSubmitAnimation(success);
          })
          .finally(() => this.busy = false);
      });
    },
    onRemoveAdditionalEmailClick(index) {
      this.leadAlertSettings.leadEmails.splice(index, 1);
    },
  },
  created: function() {
    this.$store.dispatch("leadAlertSettings").then();
  },
}
</script>

<style scoped>
.lead-alert-copy {
  min-height: 40px;
}

.label-link {
  float: right;
  font-size: .8em;
  text-decoration: underline;
}

.submit-button {
  width: 100px;
}
</style>
